import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmCloseModal } from '@/shared/ui';
import {
  formatCollaboratorNameAndRegistration,
  useCustomToast,
  type ApiResponse,
  type UserItem,
} from '@/shared/lib';
import type { Position } from '@/entities/Position';
import { usePositionUserSelection } from '@/entities/User';
import { trainingContentApi, type TrainingContentDetail } from '@/entities/Training';

export const TRAINING_CONTENT_TITLE_MAX_LENGTH = 255;

export const TRAINING_CONTENT_EDIT_NO_MEMBER_HINT =
  'Selecione ao menos um colaborador para salvar a edição.';

export const TRAINING_CONTENT_EDIT_LOAD_ERROR_MESSAGE =
  'Erro ao carregar o conteúdo. Tente novamente';
export const TRAINING_CONTENT_EDIT_SUCCESS_MESSAGE = 'Edição salva com sucesso';
export const TRAINING_CONTENT_EDIT_ERROR_MESSAGE = 'Erro ao salvar edição. Tente novamente';

const CONFIRM_CLOSE_TITLE = 'Tem certeza que deseja fechar a edição do conteúdo?';
const CONFIRM_CLOSE_DESCRIPTION = 'As mudanças realizadas não serão salvas.';

interface EditTrainingContentSnapshot {
  title: string;
  memberIds: string;
}

interface UseEditTrainingContentOptions {
  onClose: () => void;
  onUpdated: () => void;
}

export interface UseEditTrainingContentReturn {
  title: Ref<string>;
  isLoadingData: Ref<boolean>;
  isSaving: Ref<boolean>;
  positions: Ref<ApiResponse<Position[]>>;
  users: ComputedRef<ApiResponse<UserItem[]>>;
  selectedPositions: Ref<string[]>;
  selectedUsers: Ref<string[]>;
  positionsSelectedCount: ComputedRef<number>;
  usersSelectedCount: ComputedRef<number>;
  isFormValid: ComputedRef<boolean>;
  isSaveDisabled: ComputedRef<boolean>;
  hasChanges: ComputedRef<boolean>;
  memberHint: ComputedRef<string>;
  loadContent: (contentId: number) => Promise<void>;
  handleClose: () => void;
  handleSave: () => Promise<void>;
}

const sortedKey = (ids: string[]): string => [...ids].sort((a, b) => a.localeCompare(b)).join(',');

export function useEditTrainingContent(
  options: UseEditTrainingContentOptions
): UseEditTrainingContentReturn {
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const contentId = ref<number | null>(null);
  const title = ref('');
  const isLoadingData = ref(false);
  const isSaving = ref(false);
  const snapshot = ref<EditTrainingContentSnapshot | null>(null);
  /**
   * Colaboradores salvos que a lista não consegue exibir porque não têm cargo:
   * `usePositionUserSelection` só oferece quem casa com um cargo marcado, então
   * sem isto o primeiro clique em qualquer cargo os podaria da seleção e o PUT
   * revogaria o acesso deles em silêncio.
   */
  const unlistedMemberIds = ref<string[]>([]);

  const {
    positions,
    users,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    fetchPositionsAndUsers,
    resetSelection,
    initializeSelections,
  } = usePositionUserSelection({ mode: 'by-selected-positions', includeInactiveMembers: true });

  const usersWithRegistration = computed<ApiResponse<UserItem[]>>(() => ({
    ...users.value,
    data: (users.value.data ?? []).map((user) => ({
      ...user,
      name: formatCollaboratorNameAndRegistration(user.name, user.registrationNumber),
    })),
  }));

  const visibleMemberIds = computed(() => [
    ...new Set([...selectedUsers.value, ...unlistedMemberIds.value]),
  ]);

  const isFormValid = computed(
    () => title.value.trim().length > 0 && visibleMemberIds.value.length > 0
  );

  const hasChanges = computed(() => {
    if (!snapshot.value) return false;

    return (
      snapshot.value.title !== title.value.trim() ||
      snapshot.value.memberIds !== sortedKey(visibleMemberIds.value)
    );
  });

  const memberHint = computed(() =>
    visibleMemberIds.value.length === 0 ? TRAINING_CONTENT_EDIT_NO_MEMBER_HINT : ''
  );

  const isSaveDisabled = computed(() => !isFormValid.value || !hasChanges.value || isSaving.value);

  const close = () => {
    snapshot.value = null;
    contentId.value = null;
    title.value = '';
    unlistedMemberIds.value = [];
    resetSelection();
    options.onClose();
  };

  const { open: openConfirmClose, close: closeConfirmClose } = useModal({
    component: ConfirmCloseModal,
    attrs: {
      title: CONFIRM_CLOSE_TITLE,
      description: CONFIRM_CLOSE_DESCRIPTION,
      onClose() {
        closeConfirmClose();
      },
      onConfirm() {
        closeConfirmClose();
        close();
      },
    },
  });

  const applyContent = async (content: TrainingContentDetail) => {
    title.value = content.title;

    const positionIds = [
      ...new Set(
        content.visibleToMembers.flatMap((member) =>
          member.positions.map((position) => String(position.id))
        )
      ),
    ];
    const memberIds = content.visibleToMembers.map((member) => String(member.id));

    unlistedMemberIds.value = content.visibleToMembers
      .filter((member) => member.positions.length === 0)
      .map((member) => String(member.id));

    if (positionIds.length > 0) {
      await initializeSelections(positionIds, memberIds);
    }

    snapshot.value = {
      title: content.title.trim(),
      memberIds: sortedKey(memberIds),
    };
  };

  const loadContent = async (id: number) => {
    contentId.value = id;
    isLoadingData.value = true;
    snapshot.value = null;
    unlistedMemberIds.value = [];
    resetSelection();

    try {
      const [response] = await Promise.all([
        trainingContentApi.getTrainingContent(id),
        fetchPositionsAndUsers(),
      ]);
      await applyContent(response.data);
    } catch {
      showErrorToast(
        { title: TRAINING_CONTENT_EDIT_LOAD_ERROR_MESSAGE, type: 'error' },
        { timeout: false, closeButton: true }
      );
      close();
    } finally {
      isLoadingData.value = false;
    }
  };

  const handleClose = () => {
    if (hasChanges.value) {
      openConfirmClose();
      return;
    }

    close();
  };

  const handleSave = async () => {
    if (isSaveDisabled.value || contentId.value === null) return;

    isSaving.value = true;

    try {
      await trainingContentApi.updateTrainingContent(contentId.value, {
        title: title.value.trim(),
        visibleMemberIds: visibleMemberIds.value
          .map(Number)
          .filter((id) => Number.isInteger(id) && id > 0),
      });
      showSuccessToast({ title: TRAINING_CONTENT_EDIT_SUCCESS_MESSAGE, type: 'success' });
      options.onUpdated();
      close();
    } catch {
      showErrorToast(
        { title: TRAINING_CONTENT_EDIT_ERROR_MESSAGE, type: 'error' },
        { timeout: false, closeButton: true }
      );
    } finally {
      isSaving.value = false;
    }
  };

  return {
    title,
    isLoadingData,
    isSaving,
    positions,
    users: usersWithRegistration,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    isFormValid,
    isSaveDisabled,
    hasChanges,
    memberHint,
    loadContent,
    handleClose,
    handleSave,
  };
}
